/**
 * Owner-facing, view-only tenant detail — item 7 of the owner-portal spec.
 *
 * Deliberately NOT a wrapper around the admin TenantProfileDialog: that
 * component is ~8000 lines across TenantProfileDialog.tsx +
 * tenant-profile-tabs.tsx with more than a dozen scattered action triggers
 * (contract verify/send, proof upload/delete, reminder sending, inline edit
 * panels, room reassignment...), most of them NOT gated by any existing
 * prop — the NextActionBar with "Mark contract signed" / "Upload signed
 * PDF" renders unconditionally regardless of isSuperAdmin. Threading a
 * reliable readOnly flag through all of that isn't something to attempt
 * blind. This is a purpose-built, safe-by-construction view instead: it has
 * no mutation callback wired anywhere, so there is nothing for an owner to
 * accidentally trigger — same information, same five-tab shape, zero
 * actions.
 */
import { isOpenableUrl } from "@/lib/open-document-tab";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ownerTabsListClass,
  ownerTabsTriggerClass,
} from "@/components/owner/owner-v3-chrome";
import { useOwnerPortalV3 as ownerV3 } from "@/hooks/owner/useOwnerPortalV3";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  CalendarDays,
  Eye,
  FileText,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Search,
  User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import type { OwnerTenant } from "@/hooks/owner/useOwnerPropertyDetail";
import {
  ownerPaymentAsPredicateInput,
  useOwnerTenantDetail,
  useOwnerTenantPayments,
} from "@/hooks/owner/useOwnerPropertyDetail";
import {
  resolveTenantJourney,
  tenantJourneyStageLabel,
} from "@/lib/tenantJourney";
import {
  getExpectedDepositEUR,
  isPaymentEffectivelyPaid,
  resolveDepositState,
  resolvePaymentDisplayStatus,
  type PaymentDisplayStatus,
} from "@/lib/rent";
import { formatAddress } from "@/lib/v2-format";
import {
  useOwnerPropertyDocuments,
  type OwnerDocument,
} from "@/hooks/owner/useOwnerPropertyDocuments";
import { resolveTenantDocumentUrl } from "@/lib/tenant-document-url";
import { resolveStoredFileUrl } from "@/lib/storage-url";
import { DOC_TYPE_LABELS } from "@/components/admin/v2/tenant/tenant-document-labels";
import { openPendingDocumentTab } from "@/lib/open-document-tab";
import { StoredFileLink } from "@/components/shared/StoredFileLink";
import { toast } from "sonner";
import {
  computeCurrentStage,
  computeDaysOverdue,
  isPaidCurrentPeriod,
  paymentStatusForStage,
} from "@/lib/rent-reminder-templates";

const CONTRACT_TONE: Record<string, string> = {
  signed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  ready_to_sign: "bg-amber-100 text-amber-700 border-amber-200",
  awaiting_approval: "bg-amber-100 text-amber-700 border-amber-200",
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  envelope_draft: "bg-slate-100 text-slate-600 border-slate-200",
  not_ready: "bg-slate-100 text-slate-500 border-slate-200",
};

const PAYMENT_TONE: Record<string, string> = {
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  pending: "bg-slate-100 text-slate-600 border-slate-200",
  overdue_1: "bg-amber-100 text-amber-700 border-amber-200",
  overdue_2: "bg-orange-100 text-orange-700 border-orange-200",
  overdue_3: "bg-red-100 text-red-700 border-red-200",
  inkasso: "bg-red-100 text-red-700 border-red-200",
};

/** "ready_to_sign" → "Ready to sign": the English fallback for a status slug. */
function humanizeSlug(value: string): string {
  const words = value.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A status badge. The label goes through `${labelKey}.${value}` so a German
 * owner reads "Unterschrieben", not the raw slug; an unknown slug still
 * renders, humanised, rather than disappearing.
 */
function Tone({
  value,
  tones,
  labelKey,
}: {
  value: string | null | undefined;
  tones: Record<string, string>;
  labelKey: string;
}) {
  const { t } = useLanguage();
  if (!value) return <span className="text-muted-foreground">—</span>;
  const tone = tones[value] ?? "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <Badge variant="outline" className={cn("rounded-full font-medium", tone)}>
      {t(`${labelKey}.${value}`, humanizeSlug(value))}
    </Badge>
  );
}

/** Document type label: the admin profile's English names as the fallback. */
function docTypeLabel(
  type: string,
  t: (key: string, fallback?: string) => string
): string {
  return t(
    `owner.tenantView.docType.${type}`,
    DOC_TYPE_LABELS[type] ?? humanizeSlug(type)
  );
}

const ROW_STATUS_TONE: Record<PaymentDisplayStatus, string> = {
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  // Settled, like paid — the owner is still owed and reported the full rent
  // (payments.gross_amount_cents), so this is not a shortfall on their side.
  waived: "bg-teal-100 text-teal-700 border-teal-200",
  unverified: "bg-amber-100 text-amber-700 border-amber-200",
  sepa_in_progress: "bg-blue-100 text-blue-700 border-blue-200",
  overdue: "bg-red-100 text-red-700 border-red-200",
  failed: "bg-red-100 text-red-700 border-red-200",
  // COL-417: an owner must see nothing of a partial state — a month is theirs
  // to see only once it is fully settled. So a part-paid month is rendered
  // exactly as an untouched open one: same tone, same "Pending" label below.
  // This is not laziness about a missing colour; it is the requirement.
  partially_paid: "bg-slate-100 text-slate-600 border-slate-200",
  pending: "bg-slate-100 text-slate-600 border-slate-200",
};

/**
 * The exact labels tenant-profile-tabs.tsx's statusMeta shows admins/tenants
 * for the same resolvePaymentDisplayStatus value — "SEPA in progress" reads
 * as an acronym, not Title Case, so this can't reuse Tone's
 * slug-to-capitalize rendering (that's still fine for the dunning-stage
 * PAYMENT_TONE badge above, which has no acronym in its labels).
 */
function rowStatusLabel(
  status: PaymentDisplayStatus,
  t: (key: string, fallback?: string) => string
): string {
  const fallbacks: Record<PaymentDisplayStatus, string> = {
    paid: "Paid",
    waived: "Waived (referral)",
    unverified: "Unverified",
    sepa_in_progress: "SEPA in progress",
    overdue: "Overdue",
    failed: "Failed",
    // Deliberately identical to `pending` — see ROW_STATUS_TONE.
    partially_paid: "Pending",
    pending: "Pending",
  };
  const keys: Record<PaymentDisplayStatus, string> = {
    paid: "owner.rowStatus.paid",
    waived: "owner.rowStatus.waived",
    unverified: "owner.rowStatus.unverified",
    sepa_in_progress: "owner.rowStatus.sepaInProgress",
    overdue: "owner.rowStatus.overdue",
    failed: "owner.rowStatus.failed",
    // Same key as pending on purpose, so the owner cannot tell the two apart
    // even if the two translations were later edited independently.
    partially_paid: "owner.rowStatus.pending",
    pending: "owner.rowStatus.pending",
  };
  return t(keys[status], fallbacks[status]);
}

/**
 * Per-transaction status for the payments table — must resolve the same way
 * resolvePaymentDisplayStatus does for admin/tenant, so a SEPA debit in
 * flight never reads as "Pending" here while the admin/tenant profile call
 * the identical row "SEPA in progress".
 */
function PaymentRowStatusBadge({ status }: { status: PaymentDisplayStatus }) {
  const { t } = useLanguage();
  return (
    <Badge
      variant="outline"
      className={cn(
        "rounded-full font-medium whitespace-nowrap",
        ROW_STATUS_TONE[status]
      )}
    >
      {rowStatusLabel(status, t)}
    </Badge>
  );
}

function formatEuro(amount: number | null | undefined, locale: string) {
  if (amount == null) return "—";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatEuroCents(cents: number | null | undefined, locale: string) {
  if (cents == null) return "—";
  return formatEuro(cents / 100, locale);
}

function formatDate(value: string | null | undefined, locale: string) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/**
 * A "current period" only means something for someone still renting. A
 * History-tab tenant (`tenant_active: false`, no rent_due_date/rent_paid_through
 * to derive from) would otherwise fall through to "Pending" — wrong, since
 * their tenancy is over, not stalled. Left `null` so `Tone` renders the same
 * "—" it already shows for any other unknown value.
 */
export function derivePaymentStatus(ten: OwnerTenant): string | null {
  if (ten.reminders_enabled === false) return null;
  if (ten.tenant_active === false) return null;
  if (isPaidCurrentPeriod(ten)) return "paid";
  const rentDueDay = ten.rent_due_date ?? null;
  const daysOverdue = rentDueDay ? computeDaysOverdue(rentDueDay) : 0;
  return paymentStatusForStage(computeCurrentStage(daysOverdue));
}

function initialsOf(name: string | null | undefined) {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
}

export function OwnerTenantDetailDialog({
  tenant,
  propertyId,
  propertyTitle,
  open,
  onOpenChange,
}: {
  tenant: OwnerTenant | null;
  propertyId: number | null;
  propertyTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, language } = useLanguage();
  const locale = language === "de" ? "de-DE" : "en-GB";
  const [tab, setTab] = useState("overview");
  const [docSearch, setDocSearch] = useState("");
  const [docType, setDocType] = useState("all");

  const payments = useOwnerTenantPayments(
    open ? (tenant?.booking_id ?? null) : null
  );
  const full = useOwnerTenantDetail(open ? (tenant?.booking_id ?? null) : null);
  const tenantDocs = useOwnerPropertyDocuments(
    propertyId,
    open && tab === "documents"
  );

  if (!tenant) return null;

  const paymentStatus = derivePaymentStatus(tenant);
  // owner_property_documents reads only tenant_documents, so the signed
  // contract — stored on the booking, not in tenant_documents — never reached
  // this list while the admin profile shows it: owners saw one document where
  // admin shows two. Synthesised the way the admin profile does it; it signs
  // against the contracts bucket, not tenant-documents (see OwnerDocLink).
  const contractDoc: OwnerDocument | null = tenant.signed_contract_url
    ? {
        id: `signed-contract-${tenant.booking_id}`,
        title: t("owner.tenantView.signedContract", "Signed contract"),
        document_type: "contract",
        file_url: tenant.signed_contract_url,
        file_name: null,
        status: tenant.contract_status ?? "signed",
        created_at: null,
        room_title: tenant.room_title,
        tenant_name: tenant.name,
        booking_id: tenant.booking_id,
        source: "system",
      }
    : null;
  const tenantSpecificDocs = [
    ...(contractDoc ? [contractDoc] : []),
    ...(tenantDocs.data?.documents ?? []).filter(
      (d) => d.booking_id === tenant.booking_id
    ),
  ];

  const detail = full.data ?? null;

  // Filter chips are built from what this tenant actually has, so an owner
  // never sees an empty category. "all" always leads.
  const docTypes = [
    "all",
    ...Array.from(
      new Set(tenantSpecificDocs.map((d) => d.document_type ?? "other"))
    ).sort(),
  ];
  const docQuery = docSearch.trim().toLowerCase();
  const visibleTenantDocs = tenantSpecificDocs.filter((d) => {
    if (docType !== "all" && (d.document_type ?? "other") !== docType)
      return false;
    if (!docQuery) return true;
    return `${d.title ?? ""} ${d.file_name ?? ""}`
      .toLowerCase()
      .includes(docQuery);
  });

  // Same derivation the admin profile runs: the canonical deposit resolver over
  // the real payment rows, then the shared journey resolver over the raw
  // booking columns. Skipped until both queries land — a half-loaded signal
  // would render a stage that then jumps.
  const paymentRows = payments.data ?? [];
  const depositRows = paymentRows.filter((p) => p.type === "deposit");
  // resolveDepositState narrows the row type in its callback, so look the full
  // row back up by id to reach is_stripe / marked_paid_at / proof_status.
  const rowById = new Map(depositRows.map((p) => [String(p.id), p]));
  const depositPaid =
    resolveDepositState({
      depositPayments: depositRows,
      isEffectivelyPaid: (p) => {
        const row = rowById.get(String((p as { id?: string }).id ?? ""));
        if (!row) return false;
        return isPaymentEffectivelyPaid(
          ownerPaymentAsPredicateInput(row),
          () => (row.proof_status ? { status: row.proof_status } : null)
        );
      },
      expectedDepositEUR: getExpectedDepositEUR({
        monthly_rent: detail?.monthly_rent ?? tenant.monthly_rent,
        deposit_amount: detail?.deposit_amount ?? tenant.deposit_amount,
      }),
    }).status === "paid";

  const journey =
    detail && !payments.isLoading
      ? resolveTenantJourney(detail, {
          depositPaid,
          moveInProtocolSubmitted: detail.move_in_protocol_submitted,
          moveOutProtocolSubmitted: detail.move_out_protocol_submitted,
        })
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700 font-semibold">
              {initialsOf(tenant.name)}
            </div>
            <div className="min-w-0">
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {tenant.name ?? t("owner.tenantView.unnamed", "Tenant")}
                {/* The derived stage, same as the admin header pill — not the
                    raw contract_status, which is only a claim (the resolver
                    refuses to promote past an unmet gate). Falls back to the
                    contract tone until the journey signals have loaded. */}
                {journey ? (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium"
                    style={{
                      color: journey.stage.text,
                      background: journey.stage.bg,
                      borderColor: journey.stage.border,
                    }}
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ background: journey.stage.dot }}
                    />
                    {tenantJourneyStageLabel(t, journey.stageId)}
                  </span>
                ) : (
                  <Tone
                    value={tenant.contract_status}
                    tones={CONTRACT_TONE}
                    labelKey="owner.tenantView.contractStatus"
                  />
                )}
                <Badge
                  variant="outline"
                  className="rounded-full gap-1 border-border/60 text-muted-foreground font-normal"
                >
                  <Eye className="h-3 w-3" />
                  {t("owner.tenantView.viewOnly", "View only")}
                </Badge>
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                {tenant.room_title ?? "—"} · {propertyTitle}
              </p>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2 space-y-4">
          <TabsList className={ownerTabsListClass(ownerV3())}>
            <TabsTrigger
              value="overview"
              className={ownerTabsTriggerClass(ownerV3())}
            >
              {t("owner.tenantView.overview", "Overview")}
            </TabsTrigger>
            <TabsTrigger
              value="payments"
              className={ownerTabsTriggerClass(ownerV3())}
            >
              {t("owner.tenantView.payments", "Payments")}
            </TabsTrigger>
            <TabsTrigger
              value="movein"
              className={ownerTabsTriggerClass(ownerV3())}
            >
              {t("owner.tenantView.movein", "Move-in")}
            </TabsTrigger>
            <TabsTrigger
              value="moveout"
              className={ownerTabsTriggerClass(ownerV3())}
            >
              {t("owner.tenantView.moveout", "Move-out")}
            </TabsTrigger>
            <TabsTrigger
              value="documents"
              className={ownerTabsTriggerClass(ownerV3())}
            >
              {t("owner.tenantView.documents", "Documents")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="rounded-xl border p-4 space-y-3">
              <h4 className="flex items-center gap-2 text-sm font-semibold">
                <User className="h-4 w-4 text-muted-foreground" />
                {t("owner.tenantView.personalInfo", "Personal information")}
              </h4>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <IconRow
                  icon={<User className="h-3.5 w-3.5" />}
                  label={t("owner.tenantView.fullName", "Full name")}
                  value={detail?.name ?? tenant.name ?? "—"}
                />
                <IconRow
                  icon={<CalendarDays className="h-3.5 w-3.5" />}
                  label={t("owner.tenantView.dob", "Date of birth")}
                  value={
                    detail?.date_of_birth
                      ? formatDate(detail.date_of_birth, locale)
                      : null
                  }
                  missingLabel={t("owner.tenantView.missing", "Missing")}
                />
                <IconRow
                  icon={<Mail className="h-3.5 w-3.5" />}
                  label={t("owner.tenantView.email", "Email")}
                  value={detail?.email ?? tenant.email}
                  missingLabel={t("owner.tenantView.missing", "Missing")}
                />
                <IconRow
                  icon={<Phone className="h-3.5 w-3.5" />}
                  label={t("owner.tenantView.phone", "Phone")}
                  value={detail?.phone ?? tenant.phone}
                  missingLabel={t("owner.tenantView.missing", "Missing")}
                />
              </dl>
            </div>

            <div className="rounded-xl border p-4 space-y-3">
              <h4 className="flex items-center gap-2 text-sm font-semibold">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                {t("owner.tenantView.propertyLease", "Property & lease")}
              </h4>
              {detail && (
                <p className="text-xs text-muted-foreground">
                  {formatAddress(
                    detail.property_address,
                    detail.property_zip_code,
                    detail.property_city,
                    ", "
                  )}
                </p>
              )}
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <Row
                  label={t("owner.tenantView.room", "Room")}
                  value={detail?.room_title ?? tenant.room_title ?? "—"}
                />
                <Row
                  label={t("owner.tenantView.apartment", "Apartment")}
                  value={detail?.apartment_label ?? "—"}
                />
                <Row
                  label={t("owner.tenantView.moveIn", "Move-in")}
                  value={formatDate(tenant.move_in_date, locale)}
                />
                <Row
                  label={t("owner.tenantView.moveOut", "Move-out")}
                  value={formatDate(tenant.move_out_date, locale)}
                />
              </dl>
            </div>
            <div className="rounded-xl border p-4 space-y-3">
              <h4 className="text-sm font-semibold">
                {t("owner.tenantView.leaseStatus", "Lease & payment status")}
              </h4>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <Row
                  label={t("owner.tenantView.rent", "Monthly rent")}
                  value={formatEuro(tenant.monthly_rent, locale)}
                />
                <Row
                  label={t("owner.tenantView.deposit", "Deposit")}
                  value={formatEuro(tenant.deposit_amount, locale)}
                />
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-xs text-muted-foreground">
                    {t("owner.tenantView.contract", "Contract")}
                  </dt>
                  <dd>
                    <Tone
                      value={tenant.contract_status}
                      tones={CONTRACT_TONE}
                      labelKey="owner.tenantView.contractStatus"
                    />
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-xs text-muted-foreground">
                    {t("owner.tenantView.payment", "Payment")}
                  </dt>
                  <dd>
                    {tenant.reminders_enabled === false ? (
                      <Badge
                        variant="outline"
                        className="rounded-full bg-slate-100 text-slate-500 border-slate-200"
                      >
                        {t("owner.detail.remindersOff", "Reminders off")}
                      </Badge>
                    ) : (
                      <Tone
                        value={paymentStatus}
                        tones={PAYMENT_TONE}
                        labelKey="owner.tenantView.paymentStatus"
                      />
                    )}
                  </dd>
                </div>
              </dl>
              {tenant.signed_contract_url && (
                // Signed on click (COL-436): the stored value is a public URL
                // into the contracts bucket, which is going private.
                <StoredFileLink
                  value={tenant.signed_contract_url}
                  bucket="contracts"
                  errorText={t(
                    "owner.doc.couldNotOpen",
                    "Could not open this document."
                  )}
                  className="text-sm text-teal-600 hover:underline"
                >
                  {t("owner.tenantView.viewContract", "View signed contract")}
                </StoredFileLink>
              )}
            </div>
          </TabsContent>

          <TabsContent value="payments">
            {payments.isLoading ? (
              <p className="text-sm text-muted-foreground">
                {t("owner.tenantView.loading", "Loading…")}
              </p>
            ) : (payments.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                {t("owner.tenantView.noPayments", "No payments recorded yet.")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("owner.tenantView.type", "Type")}</TableHead>
                    <TableHead className="text-right">
                      {t("owner.tenantView.amount", "Amount")}
                    </TableHead>
                    <TableHead>
                      {t("owner.tenantView.status", "Status")}
                    </TableHead>
                    <TableHead>{t("owner.tenantView.due", "Due")}</TableHead>
                    <TableHead>{t("owner.tenantView.paid", "Paid")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(payments.data ?? []).map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        {t(
                          `owner.tenantView.paymentType.${p.type}`,
                          humanizeSlug(p.type)
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatEuroCents(p.amount_cents, locale)}
                      </TableCell>
                      <TableCell>
                        <PaymentRowStatusBadge
                          status={resolvePaymentDisplayStatus(
                            {
                              ...ownerPaymentAsPredicateInput(p),
                              paid_at: p.paid_at,
                              metadata: p.metadata,
                            },
                            () =>
                              p.proof_status ? { status: p.proof_status } : null
                          )}
                        />
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {formatDate(p.due_date, locale)}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {formatDate(p.paid_at, locale)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          {/* Move-in / move-out as the milestone checklists the admin tabs
              show, each line carrying its own evidence date — the same columns
              the journey gates read, so the tab can never disagree with the
              stage in the header. */}
          <TabsContent value="movein" className="space-y-2">
            <Milestone
              label={t("owner.tenantView.msContract", "Contract signed")}
              at={detail?.contract_signed_at}
              done={!!detail?.contract_signed_at}
              locale={locale}
              t={t}
            />
            <Milestone
              label={t("owner.tenantView.msMoveInDate", "Move-in date")}
              at={tenant.move_in_date}
              done={!!tenant.move_in_date}
              locale={locale}
              t={t}
            />
            <Milestone
              label={t("owner.tenantView.msKeys", "Keys handed over")}
              at={detail?.key_picked_up_at}
              done={!!detail?.key_picked_up_at}
              locale={locale}
              t={t}
            />
            <Milestone
              label={t("owner.tenantView.msProtocolIn", "Handover protocol")}
              at={null}
              done={!!detail?.move_in_protocol_submitted}
              locale={locale}
              t={t}
            />
            <Milestone
              label={t(
                "owner.tenantView.msMoveInConfirmed",
                "Move-in confirmed"
              )}
              at={detail?.move_in_confirmed_at}
              done={!!detail?.move_in_confirmed_at}
              locale={locale}
              t={t}
            />
          </TabsContent>

          <TabsContent value="moveout" className="space-y-2">
            <Milestone
              label={t("owner.tenantView.msNotice", "Notice received")}
              at={detail?.move_out_notice_at}
              done={!!detail?.move_out_notice_at}
              locale={locale}
              t={t}
            />
            <Milestone
              label={t("owner.tenantView.msMoveOutDate", "Move-out date")}
              at={tenant.move_out_date}
              done={!!tenant.move_out_date}
              locale={locale}
              t={t}
            />
            <Milestone
              label={t("owner.tenantView.msProtocolOut", "Handover protocol")}
              at={null}
              done={!!detail?.move_out_protocol_submitted}
              locale={locale}
              t={t}
            />
            <Milestone
              label={t("owner.tenantView.msKeysBack", "Keys returned")}
              at={detail?.key_returned_at}
              done={!!detail?.key_returned_at}
              locale={locale}
              t={t}
            />
          </TabsContent>

          <TabsContent value="documents" className="space-y-6">
            <div className="space-y-3">
              {/* Same shape as the admin Documents tab — count, search, type
                  filters, card grid — minus the upload dropzone and the
                  per-card action menu, which are the tab's only mutations. */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h4 className="flex items-center gap-2 text-sm font-semibold">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  {t("owner.tenantView.tenantDocs", "Documents")}
                  <Badge
                    variant="outline"
                    className="rounded-full bg-muted font-medium text-muted-foreground"
                  >
                    {tenantSpecificDocs.length}
                  </Badge>
                </h4>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={docSearch}
                    onChange={(e) => setDocSearch(e.target.value)}
                    placeholder={t(
                      "owner.tenantView.searchDocs",
                      "Search documents…"
                    )}
                    className="h-9 w-56 rounded-xl border border-border/70 bg-background pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40"
                  />
                </div>
              </div>

              {docTypes.length > 1 && (
                <div className="flex flex-wrap gap-1.5">
                  {docTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setDocType(type)}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                        docType === type
                          ? "border-teal-200 bg-teal-100 text-teal-700"
                          : "border-border/70 text-muted-foreground hover:bg-muted"
                      )}
                    >
                      {type === "all"
                        ? t("owner.tenantView.docsAll", "All")
                        : docTypeLabel(type, t)}
                      <span className="opacity-60">
                        {type === "all"
                          ? tenantSpecificDocs.length
                          : tenantSpecificDocs.filter(
                              (d) => (d.document_type ?? "other") === type
                            ).length}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {tenantDocs.isLoading ? (
                <p className="text-sm text-muted-foreground">
                  {t("owner.tenantView.loading", "Loading…")}
                </p>
              ) : visibleTenantDocs.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  {t(
                    "owner.tenantView.noTenantDocs",
                    "No documents for this tenant yet."
                  )}
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {visibleTenantDocs.map((doc) => (
                    <OwnerDocLink key={doc.id} doc={doc} locale={locale} />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One tenant document in the owner Documents tab. `doc.file_url` from
 * `owner_property_documents` may be a bare or bucket-qualified Storage path
 * (e.g. handover protocols filed by COL-425) rather than a browser URL, so it
 * has to be signed on click instead of used directly as an `<a href>` — a raw
 * path there resolves against the SPA route and 404s.
 */
function OwnerDocLink({ doc, locale }: { doc: OwnerDocument; locale: string }) {
  // The synthesised signed-contract row stores a contracts-bucket path, which
  // the tenant-documents resolver would not sign.
  const isSignedContract = doc.id.startsWith("signed-contract-");
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);

  const open = async () => {
    if (!doc.file_url || loading) return;
    // Open the tab synchronously inside the click's user gesture so it isn't
    // blocked by the popup blocker, then point it at the resolved URL once
    // signed.
    const tab = openPendingDocumentTab();
    setLoading(true);
    try {
      const url = isSignedContract
        ? await resolveStoredFileUrl(doc.file_url, "contracts")
        : await resolveTenantDocumentUrl(doc.file_url);
      if (url) {
        tab.navigate(url);
      } else {
        tab.cancel();
        toast.error(
          t("owner.doc.couldNotOpen", "Could not open this document.")
        );
      }
    } catch {
      tab.cancel();
      toast.error(t("owner.doc.couldNotOpen", "Could not open this document."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void open()}
      disabled={!doc.file_url || loading}
      className={cn(
        "flex items-start gap-3 rounded-xl border p-3 text-left transition-colors",
        doc.file_url ? "hover:bg-muted/50" : "pointer-events-none opacity-60"
      )}
    >
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <FileText className="h-4 w-4 text-muted-foreground" />
        )}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">
          {doc.title ?? doc.file_name ?? "—"}
        </span>
        <span className="mt-1 flex flex-wrap items-center gap-1.5">
          {doc.document_type && (
            <Badge
              variant="outline"
              className="rounded-full bg-muted/70 text-[11px] font-medium text-muted-foreground"
            >
              {docTypeLabel(doc.document_type, t)}
            </Badge>
          )}
          {doc.status && (
            <Badge
              variant="outline"
              className={cn(
                "rounded-full text-[11px] font-medium",
                doc.status === "signed" || doc.status === "approved"
                  ? "border-emerald-200 bg-emerald-100 text-emerald-700"
                  : "border-amber-200 bg-amber-100 text-amber-700"
              )}
            >
              {t(
                `owner.tenantView.docStatus.${doc.status}`,
                humanizeSlug(doc.status)
              )}
            </Badge>
          )}
        </span>
        {doc.created_at && (
          <span className="mt-1 block text-[11px] text-muted-foreground">
            {formatDate(doc.created_at, locale)}
          </span>
        )}
      </span>
    </button>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium text-right">{value}</dd>
    </div>
  );
}

/**
 * One move-in / move-out milestone: what it is, when it happened, and whether
 * it has happened at all. `at` is null for milestones evidenced by a row rather
 * than a timestamp (the handover protocols), which show only the state.
 */
function Milestone({
  label,
  at,
  done,
  locale,
  t,
}: {
  label: string;
  at: string | null | undefined;
  done: boolean;
  locale: string;
  t: (key: string, fallback: string) => string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border px-4 py-3">
      <span className="text-sm font-medium">{label}</span>
      <span className="flex items-center gap-3">
        {at && (
          <span className="text-sm text-muted-foreground tabular-nums">
            {formatDate(at, locale)}
          </span>
        )}
        <Badge
          variant="outline"
          className={cn(
            "rounded-full font-medium",
            done
              ? "border-emerald-200 bg-emerald-100 text-emerald-700"
              : "border-slate-200 bg-slate-100 text-slate-500"
          )}
        >
          {done
            ? t("owner.tenantView.done", "Done")
            : t("owner.tenantView.pending", "Pending")}
        </Badge>
      </span>
    </div>
  );
}

/**
 * Labelled field with a leading glyph. An absent value renders a "Missing"
 * chip rather than a dash, so a gap the owner may need to chase reads as a gap
 * instead of as an empty row.
 */
function IconRow({
  icon,
  label,
  value,
  missingLabel,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
  missingLabel?: string;
}) {
  const missing = !value;
  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2",
        missing && missingLabel
          ? "border-amber-200 bg-amber-50/60"
          : "border-transparent bg-muted/40"
      )}
    >
      <dt className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        <span className="text-muted-foreground/70">{icon}</span>
        {label}
      </dt>
      <dd className="mt-0.5 text-sm font-medium break-words">
        {missing ? (
          missingLabel ? (
            <Badge
              variant="outline"
              className="rounded-full border-amber-300 bg-amber-100 text-amber-800 font-medium"
            >
              {missingLabel}
            </Badge>
          ) : (
            "—"
          )
        ) : (
          value
        )}
      </dd>
    </div>
  );
}
